import { ReactNode, useEffect, useState, memo, lazy, Suspense } from "react";
import { useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { CACHE_TIMES } from "@/lib/queryClient";
import { Header } from "./header";
import { Footer } from "./footer";
import { MetaTags } from "./meta-tags";
import { Banner } from "./banner";
import { AnnouncementBar } from "./announcement-system";
import { VacationBanner } from "./vacation-banner";
const PWAInstallPrompt = lazy(() => import("./pwa-install-prompt").then(m => ({ default: m.PWAInstallPrompt })));
import { getPageMeta } from "@/lib/seo";
import { useLayoutContext } from "@/contexts/layout-context";

const ChatbotWidget = lazy(() => import("./chatbot/chatbot-widget").then(m => ({ default: m.ChatbotWidget })));

interface PublicLayoutShellProps {
  children: ReactNode;
}

const MemoizedHeader = memo(Header);
const MemoizedFooter = memo(Footer);
const MemoizedBanner = memo(Banner);
const MemoizedAnnouncementBar = memo(AnnouncementBar);
const MemoizedVacationBanner = memo(VacationBanner);

export function PublicLayoutShell({ children }: PublicLayoutShellProps) {
  const [location] = useLocation();
  const { publicOptions } = useLayoutContext();

  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/public/settings"],
    staleTime: CACHE_TIMES.catalog,
  });

  const isChatbotEnabled = settings?.chatbot_enabled === "true";

  const [chatbotReady, setChatbotReady] = useState(false);
  useEffect(() => {
    if (!isChatbotEnabled || chatbotReady) return;
    const w = typeof window !== "undefined" ? (window as any) : null;
    if (!w) return;
    const trigger = () => setChatbotReady(true);
    const events: Array<keyof WindowEventMap> = ["pointerdown", "keydown", "scroll", "touchstart"];
    const onAny = () => { trigger(); events.forEach(e => w.removeEventListener(e, onAny)); };
    events.forEach(e => w.addEventListener(e, onAny, { passive: true, once: false }));
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    if (typeof w.requestIdleCallback === "function") {
      idleId = w.requestIdleCallback(() => trigger(), { timeout: 5000 });
    } else {
      timeoutId = w.setTimeout(trigger, 4000);
    }
    return () => {
      events.forEach(e => w.removeEventListener(e, onAny));
      if (idleId !== undefined && typeof w.cancelIdleCallback === "function") w.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) w.clearTimeout(timeoutId);
    };
  }, [isChatbotEnabled, chatbotReady]);

  const fallbackMeta = getPageMeta(location, settings);

  return (
    <>
      <MetaTags 
        pageUrl={location} 
        fallback={{
          title: fallbackMeta?.title,
          description: fallbackMeta?.description,
          keywords: fallbackMeta?.keywords
        }} 
      />
      
      <div className="flex flex-col min-h-screen min-h-[100dvh]">
        <MemoizedAnnouncementBar />
        <MemoizedVacationBanner />
        <MemoizedBanner />
        <MemoizedHeader />
        <main id="main-content" tabIndex={-1} className="flex-1 focus:outline-none">
          {children}
        </main>
        {publicOptions.preFooter}
        {/* When the page has a fixed mobile CTA bar, pad below the footer so
            its last rows stay reachable above the bar (safe-area aware). */}
        <div
          className={
            publicOptions.mobileStickyBar === "md"
              ? "bg-[hsl(var(--footer-bg))] pb-[calc(5rem+env(safe-area-inset-bottom,0px))] md:pb-0"
              : publicOptions.mobileStickyBar === "lg"
                ? "bg-[hsl(var(--footer-bg))] pb-[calc(5rem+env(safe-area-inset-bottom,0px))] lg:pb-0"
                : undefined
          }
        >
          <MemoizedFooter />
        </div>
        
        {isChatbotEnabled && chatbotReady && (
          <Suspense fallback={null}>
            <ChatbotWidget />
          </Suspense>
        )}
        
        <Suspense fallback={null}>
          <PWAInstallPrompt environment="public" />
        </Suspense>
      </div>
    </>
  );
}
